import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { userAPI } from '../api/userAPI';
import { regionAPI } from '../api/regionAPI';
import { formatPhone, timeAgo } from '../utils/format';
import { Avatar } from '../components/Avatar';
import { SearchInput } from '../components/SearchInput';
import { FilterPopover } from '../components/FilterPopover';
import { FilterBar } from '../components/FilterBar';

const formatRole = (role) => {
    return role
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const ROLE_COLORS = {
    admin: 'bg-red-500 text-white',
    pastor: 'bg-violet-500 text-white',
    regional_leader: 'bg-blue-500 text-white',
    regional_assistant: 'bg-emerald-500 text-white',
    intake: 'bg-amber-500 text-white',
};

export function UserManagement() {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [q, setQ] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        const timer = setTimeout(() => { setQ(searchInput); }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const regionFor = (id) => regions.find((r) => r.id === id);

    const regionBadge = (id) => {
        const r = regionFor(id);
        if (!r) return <span className="text-slate-400">-</span>;
        const color = r.color || '#6b7280';
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: color + '1A', color }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                {r.name}
            </span>
        );
    };

    useEffect(() => {
        if (!isAdmin) return;
        regionAPI.getRegions().then((res) => {
            const payload = res.data.data || {};
            setRegions(payload.items || []);
        }).catch(() => {});
    }, [isAdmin]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = { per_page: 1000 };
            if (q) params.q = q;
            if (roleFilter) params.role = roleFilter;
            if (regionFilter) params.region_id = regionFilter;
            const response = await userAPI.getUsers(params);
            const payload = response.data.data || {};
            setUsers(payload.items || []);
            setError(null);
        } catch (err) {
            setError('Failed to load leaders');
        } finally {
            setLoading(false);
        }
    }, [q, roleFilter, regionFilter]);

    useEffect(() => {
        if (isAdmin) fetchUsers();
    }, [isAdmin, fetchUsers]);

    if (!isAdmin) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] p-4">
                <Card>
                    <CardBody>
                        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Access Denied</h2>
                        <p className="text-sm text-slate-500 mb-4">Only administrators can access leader management.</p>
                        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this leader?')) {
            try {
                await userAPI.deleteUser(id);
                setSuccess('Leader deleted successfully');
                fetchUsers();
            } catch (err) {
                setError('Failed to delete leader');
            }
        }
    };

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Leader Management</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Manage system leaders and their roles</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                    <Button onClick={() => navigate('/users/new')}>+ Add Leader</Button>
                </div>
            </div>

            <FilterBar
                search={
                    <SearchInput
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search name or email"
                    />
                }
                filters={<>
                    <FilterPopover
                        label="Role"
                        value={roleFilter}
                        onChange={setRoleFilter}
                        options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'pastor', label: 'Pastor' },
                            { value: 'regional_leader', label: 'Regional Leader' },
                            { value: 'regional_assistant', label: 'Regional Assistant' },
                            { value: 'intake', label: 'Intake' },
                        ]}
                        allLabel="All Roles"
                    />
                    <FilterPopover
                        label="Region"
                        value={regionFilter}
                        onChange={setRegionFilter}
                        options={regions.map((r) => ({ value: r.id, label: r.name }))}
                        allLabel="All Regions"
                    />
                </>}
                activeFilterCount={(roleFilter ? 1 : 0) + (regionFilter ? 1 : 0)}
                onClearFilters={() => { setRoleFilter(''); setRegionFilter(''); }}
            />

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <Card>
                <CardHeader>
                    <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Leaders ({users.length})</h2>
                </CardHeader>
                <CardBody>
                    {loading ? (
                        <p className="text-center text-slate-500 py-6">Loading leaders...</p>
                    ) : users.length === 0 ? (
                        <p className="text-center text-slate-500 py-6">No leaders found</p>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b-2 border-slate-200 dark:border-slate-700">
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Email</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Roles</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Login</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Region</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr key={u.id} className="even:bg-slate-50/50 dark:even:bg-slate-800/30 hover:bg-slate-100/50 dark:hover:bg-slate-700/30 transition-colors">
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">
                                                    <Link to={`/users/${u.id}`} className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600 font-medium">
                                                        <Avatar name={u.name} photoUrl={u.photo_url} size="sm" />{u.name}
                                                    </Link>
                                                </td>
                                                <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">
                                                    {u.email ? <a href={`mailto:${u.email}`} className="text-accent-500 hover:text-accent-600">{u.email}</a> : <span className="text-slate-400 italic">no email</span>}
                                                </td>
                                                <td className="px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700/50">
                                                    {u.phone ? <a href={`tel:${u.phone}`} className="text-accent-500 hover:text-accent-600">{formatPhone(u.phone)}</a> : '-'}
                                                </td>
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">
                                                    <div className="flex flex-wrap gap-1">
                                                        {(u.roles || []).map((r) => (
                                                            <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[r] || 'bg-slate-200 text-slate-700'}`}>
                                                                {formatRole(r)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        u.status === 'pending'
                                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                    }`}>
                                                        {u.status === 'pending' ? (u.email ? 'Pending' : 'Pending (no email)') : 'Active'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">
                                                    {u.last_login_at
                                                        ? <span className="text-slate-700 dark:text-slate-300">{timeAgo(u.last_login_at)}</span>
                                                        : <span className="text-amber-600 dark:text-amber-400 font-medium">Never</span>}
                                                </td>
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">{regionBadge(u.region_id)}</td>
                                                <td className="px-3 py-2.5 text-sm border-b border-slate-100 dark:border-slate-700/50">
                                                    <div className="flex gap-1.5">
                                                        <Button size="sm" variant="secondary" onClick={() => navigate(`/users/${u.id}`)}>Edit</Button>
                                                        <Button size="sm" variant="danger" onClick={() => handleDelete(u.id)}>Delete</Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="sm:hidden space-y-3">
                                {users.map((u) => (
                                    <div key={u.id} className="card-base p-4 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Link to={`/users/${u.id}`} className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600 font-medium text-sm">
                                                <Avatar name={u.name} photoUrl={u.photo_url} size="sm" />{u.name}
                                            </Link>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                u.status === 'pending'
                                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                            }`}>
                                                {u.status === 'pending' ? (u.email ? 'Pending' : 'Pending (no email)') : 'Active'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500 font-medium">Email</span>
                                            <span>{u.email ? <a href={`mailto:${u.email}`} className="text-accent-500">{u.email}</a> : <em className="text-slate-400">none</em>}</span>
                                        </div>
                                        {u.phone && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500 font-medium">Phone</span>
                                                <span><a href={`tel:${u.phone}`} className="text-accent-500">{formatPhone(u.phone)}</a></span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-sm items-center">
                                            <span className="text-slate-500 font-medium">Roles</span>
                                            <div className="flex flex-wrap gap-1">
                                                {(u.roles || []).map((r) => (
                                                    <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[r] || 'bg-slate-200 text-slate-700'}`}>
                                                        {formatRole(r)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex justify-between text-sm items-center">
                                            <span className="text-slate-500 font-medium">Region</span>
                                            {regionBadge(u.region_id)}
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500 font-medium">Last Login</span>
                                            {u.last_login_at
                                                ? <span className="text-slate-700 dark:text-slate-300">{timeAgo(u.last_login_at)}</span>
                                                : <span className="text-amber-600 dark:text-amber-400 font-medium">Never</span>}
                                        </div>
                                        <div className="flex gap-2 pt-1">
                                            <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`/users/${u.id}`)}>Edit</Button>
                                            <Button size="sm" variant="danger" className="flex-1" onClick={() => handleDelete(u.id)}>Delete</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}
